import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  filterTasksByQuery,
  getTaskName,
  resolveSelectedTask,
} from '../utils/taskSearchSelect';

const DEFAULT_TYPE_LABELS = {
  document: 'Тех. заказчик',
  hr: 'HR',
  procurement: 'Закупки',
  construction: 'Строительство',
  marketing: 'Маркетинг',
  preconstruction: 'До начала строительства',
  tz_construction: 'Строительство (ТЗ)',
};

/**
 * Раскрывающийся выбор задачи с поиском по названию / этапу.
 */
const TaskSearchSelect = ({
  tasks = [],
  value = null,
  onChange,
  placeholder = 'Выберите задачу',
  className = '',
  testIdPrefix = 'task-search',
  excludeId = null,
  typeLabels = DEFAULT_TYPE_LABELS,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);
  const searchRef = useRef(null);

  const selected = useMemo(
    () => resolveSelectedTask(tasks, value),
    [tasks, value]
  );

  const filteredTasks = useMemo(
    () => filterTasksByQuery(tasks, query, { excludeId }),
    [tasks, query, excludeId]
  );

  const grouped = useMemo(() => {
    const map = new Map();
    filteredTasks.forEach((t) => {
      const key = t.schedule_type || 'other';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(t);
    });
    return [...map.entries()];
  }, [filteredTasks]);

  useEffect(() => {
    if (!open) return undefined;

    const onDocMouseDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setQuery('');
      }
    };

    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open && searchRef.current) {
      searchRef.current.focus();
    }
  }, [open]);

  const selectTask = (taskId) => {
    onChange?.(taskId);
    setOpen(false);
    setQuery('');
  };

  const isValueActive = (taskId) => {
    if (value == null || value === '') return false;
    return Number(taskId) === Number(value);
  };

  return (
    <div
      className={`po-city-select city-search-select task-search-select ${className}`.trim()}
      ref={rootRef}
    >
      <button
        type="button"
        className="btn btn-outline-secondary po-city-select__trigger"
        data-testid={`${testIdPrefix}-trigger`}
        aria-expanded={open}
        aria-haspopup="listbox"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
      >
        <span className="city-search-select__label">
          {selected ? (
            <span className="task-search-select__selected-text">{getTaskName(selected)}</span>
          ) : (
            placeholder
          )}
        </span>
        <span className="po-city-select__chevron" aria-hidden>{open ? '▴' : '▾'}</span>
      </button>

      {open && (
        <div className="glass-dropdown po-city-select__dropdown" role="listbox">
          <div className="po-city-select__search">
            <input
              ref={searchRef}
              type="search"
              className="form-control"
              placeholder="Поиск по названию…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid={`${testIdPrefix}-search`}
            />
          </div>
          <div className="po-city-select__list">
            {filteredTasks.length === 0 ? (
              <div className="po-city-select__empty">Ничего не найдено</div>
            ) : (
              grouped.map(([typeKey, groupTasks]) => (
                <div key={typeKey} className="task-search-select__group">
                  <div className="task-search-select__group-label">
                    {typeLabels[typeKey] || typeKey}
                  </div>
                  {groupTasks.map((task) => {
                    const isActive = isValueActive(task.id);
                    return (
                      <button
                        key={task.id}
                        type="button"
                        role="option"
                        aria-selected={isActive}
                        data-testid={`${testIdPrefix}-option-${task.id}`}
                        className={`po-city-select__option city-search-select__option${isActive ? ' city-search-select__option--active' : ''}`}
                        onClick={() => selectTask(task.id)}
                      >
                        <span className="task-search-select__option-main">
                          <span>{getTaskName(task)}</span>
                          {task.construction_stage && (
                            <span className="task-search-select__option-meta">
                              {task.construction_stage}
                            </span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskSearchSelect;
